import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { TCComponentRawMaterialInformation } from "../../../../tc-models/tc-req-dto/tc-component-details.req_dto";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { useParams } from "react-router-dom";


export interface RawMaterialsInfoProps {
  /** Certified + Non-Certified Weight from Stepper 1 (Component Information) – total kg to allocate across raw materials */
  certifiedAndNonCertifiedWeight?: number | string | null;
  rawMaterialInfoData: TCComponentRawMaterialInformation[];
  setRawMaterialInfoData: React.Dispatch<
    React.SetStateAction<TCComponentRawMaterialInformation[]>
  >;
}

// Define Yup validation schema for each row


const RawMaterialInfo: React.FC<RawMaterialsInfoProps> = ({
  certifiedAndNonCertifiedWeight,
  rawMaterialInfoData,
  setRawMaterialInfoData,
}) => {
  const displayWeight =
    certifiedAndNonCertifiedWeight != null &&
    certifiedAndNonCertifiedWeight !== '' &&
    !Number.isNaN(Number(certifiedAndNonCertifiedWeight))
      ? Number(certifiedAndNonCertifiedWeight)
      : 0;
  const { notify } = useNotification()
  const { isRestrictPopUpSave, setRestrictPopUpSave } = useTransactionCertificateStore();
  const { executeGet } = useApi();
  const { TE_ID } = useParams();
  const [rawMaterials, setRawMaterials] = useState([]);
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<{ standard: string; material_code: string }[]>([]);
  // null = not loaded yet; [] = loaded but empty (safe fallback)
  const [tcStandardList, setTcStandardList] = useState<any[] | null>(null);
  const [data, setData] = useState<TCComponentRawMaterialInformation[]>(
    rawMaterialInfoData
  ); // Initialize with prop data
  const [tableData, setTableData] = useState<TCComponentRawMaterialInformation[]>(
    rawMaterialInfoData
  ); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { updateField } = useFormActions("component-details");

  const validationSchema = Yup.object().shape({
    tcComponentRawMaterialCode: Yup.string().nullable(),
    tcComponentRawMaterialPercentage: Yup.number()
      .typeError("Percentage must be a number")
      .positive("Percentage must be positive").max(100, "Percentage cannot exceed 100")
      .test(
        'is-decimal',
        'Percentage must have at most 5 decimal places',
        (value: any) => {
          if (value === undefined || value === null) return true;
          return /^[0-9]+(\.[0-9]{1,5})?$/.test(value.toString());
        }
      )
      .test(
        'percentage-equals-100',
        'percentage-equals-100',
        function (value: any) {
          if (editingRowIndex > -1) {
            let total = data.reduce((sum, item, index) => {
              if (index === editingRowIndex) return sum;
              return sum + parseFloat(item.tcComponentRawMaterialPercentage);
            }, 0);
            if ((total + (Number(value) || 0)) > 100) {
              notify("Total raw material percentage cannot exceed 100%.", "error");
              return false;
            }
          }
          return true;
  
        }
      )
      .nullable(),
    tcComponentRawMaterialCertified: Yup.string().nullable(),
    tcComponentRawMaterialAllocatedWeight: Yup.number()
      .typeError("Weight must be a number")
      .min(0, "Weight cannot be negative")
      .nullable(),
  }).test(
    'at-least-one',
    'At least one field is required',
    function (values) {
      return values.tcComponentRawMaterialCode || values.tcComponentRawMaterialPercentage ? true : this.createError({ path: 'field1', message: 'At least one field is required' });
    }
   );
  // Calculate allocated weight for all rows based on percentage when data is loaded/changed
  useEffect(() => {
    if (rawMaterialInfoData && rawMaterialInfoData.length > 0) {
      const dataWithAllocatedWeights = rawMaterialInfoData.map((item) => ({
        ...item,
        tcComponentRawMaterialAllocatedWeight: allocatedWeightFromPercentage(
          item.tcComponentRawMaterialPercentage
        ),
      }));
      setData(dataWithAllocatedWeights);
      setTableData(dataWithAllocatedWeights);
      // on the Standard Information step reflects the correct value when editing.
      const hasChanged = dataWithAllocatedWeights.some((item, i) =>
        Number(item.tcComponentRawMaterialAllocatedWeight) !==
        Number((rawMaterialInfoData[i] as any).tcComponentRawMaterialAllocatedWeight)
      );
      if (hasChanged) {
        setRawMaterialInfoData(dataWithAllocatedWeights as any);
      }
    }
  }, [rawMaterialInfoData, displayWeight]);

  useEffect(() => {
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes()

        Utills.cl(`result getProductDetails ${JSON.stringify(result)}`)

        const rawMaterials = result.data.map((t: any) => ({ value: t.code_1, label: `${t.description_1} (${t.code_1})` }))
        Utills.cl(`rawMaterials ${JSON.stringify(rawMaterials)}`)
        setRawMaterials(rawMaterials);
      } catch (err) {
        Utills.cle(`Error in Fetching Organic Farm Standard Code ${err}`)
      }
    };
    getRawMaterialCodes()
  }, []);

  useEffect(() => {
    const fetchCertifiedRawMaterials = async () => {
      try {
        const response = await executeGet(TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES);
        // API may return array as body (response.data) or wrapped (response.data.data)
        const raw = response?.data;
        const list = Array.isArray(raw) ? raw : (raw?.data && Array.isArray(raw.data) ? raw.data : []);
        if (list.length > 0) {
          setCertifiedRawMaterialCodes(list);
        }
      } catch (err) {
        Utills.cle(`Error fetching certified raw material codes ${err}`);
      }
    };
    fetchCertifiedRawMaterials();
  }, []);

  useEffect(() => {
    const fetchTcStandard = async () => {
      if (!TE_ID) return;
      try {
        const response: any = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS, {
          tcId: TE_ID,
        });
        if (response?.data?.tcStandard) {
          setTcStandardList(response.data.tcStandard);
        }
      } catch (err) {
        console.error("Failed to fetch TC base details for standards:", err);
      }
    };

    fetchTcStandard();
  }, []);
  const formik = useFormik<TCComponentRawMaterialInformation>({
    initialValues: {
      tcComponentRawMaterialOrder: "",
      tcComponentRawMaterialPercentage: '',
      tcComponentRawMaterialCode: "",
      tcComponentRawMaterialCertified: "",
      tcComponentRawMaterialAllocatedWeight: '',
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  /** Allocated weight (kg) = (displayWeight * percentage) / 100. e.g. 1000 kg, 40% → 400 kg */
  const allocatedWeightFromPercentage = (pct: number | string | null | undefined): number => {
    const n = Number(pct);
    if (Number.isNaN(n) || displayWeight <= 0) return 0;
    return Math.round((displayWeight * n) / 100 * 1e5) / 1e5;
  };

  const getTotalPercentage = (rows: TCComponentRawMaterialInformation[], excludeIndex: number = -1): number => {
    return rows.reduce((sum, row, index) => {
      if (index === excludeIndex) return sum;
      const p = row.tcComponentRawMaterialPercentage;
      const num = typeof p === "number" ? p : parseFloat(String(p));
      return sum + (Number.isNaN(num) ? 0 : num);
    }, 0);
  };

  /** Current total percentage including the row being edited (if any) */
  const getCurrentTotalPercentage = (): number => {
    if (editingRowIndex >= 0) {
      const otherPct = getTotalPercentage(data, editingRowIndex);
      const currentPct = Number(formik.values.tcComponentRawMaterialPercentage) || 0;
      return otherPct + currentPct;
    }
    return getTotalPercentage(data);
  };

  /** True when total percentage is 100% (disables Add Raw Material) */
  const isTotalPercentage100 = (): boolean => {
    const total = getCurrentTotalPercentage();
    return Math.abs(total - 100) < 1e-6;
  };

  const isCertifiedRawMaterialCode = (
    materialCode: string
  ): boolean | null => {
    const standard =
      certifiedRawMaterialCodes.find((item) => item.material_code === materialCode)
        ?.standard || "";

    if (!standard) return false;

    if (String(standard).toUpperCase() === "NA") return false;

    if (tcStandardList === null) return null;
    if (!tcStandardList.length) return false;

    return tcStandardList.some((std: any) => String(standard).includes(std));
  };

  // Recompute Certified column once standards + certified RM codes are ready.
  useEffect(() => {
    if (tcStandardList === null) return;
    if (!certifiedRawMaterialCodes?.length) return;

    const recomputed = data.map((row) => {
      const code = row.tcComponentRawMaterialCode || "";
      if (!code) return row;
      const res = isCertifiedRawMaterialCode(code);
      const certified = res === null ? "" : res ? "Yes" : "No";
      return row.tcComponentRawMaterialCertified === certified
        ? row
        : { ...row, tcComponentRawMaterialCertified: certified };
    });

    const changed = recomputed.some((r, i) => r !== data[i]);
    if (changed) {
      setData(recomputed);
      setTableData(recomputed);
      setRawMaterialInfoData(recomputed);

      if (editingRowIndex >= 0) {
        const currentCode = formik.values.tcComponentRawMaterialCode;
        if (currentCode) {
          const res = isCertifiedRawMaterialCode(currentCode);
          const certified = res === null ? "" : res ? "Yes" : "No";
          formik.setFieldValue("tcComponentRawMaterialCertified", certified);
        }
      }
    }
  }, [tcStandardList, certifiedRawMaterialCodes]);

  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    if (columnId === "tcComponentRawMaterialPercentage") {
      const pctNum = Number(value);
      if (!Number.isNaN(pctNum) && pctNum > 0) {
        const otherPct = getTotalPercentage(data, rowIndex);
        const newTotalPct = otherPct + pctNum;
        if (newTotalPct > 100) {
          const maxPct = 100 - otherPct;
          const maxPctFormatted = maxPct % 1 === 0 ? String(maxPct) : maxPct.toFixed(5);
          notify(
            `Total raw material percentage cannot exceed 100%. Current total from other rows: ${otherPct}%. You can enter up to ${maxPctFormatted}% in this row.`,
            "error"
          );
          return;
        }
      }
      const allocated = allocatedWeightFromPercentage(value);
      formik.setFieldValue("tcComponentRawMaterialPercentage", value);
      formik.setFieldValue("tcComponentRawMaterialAllocatedWeight", allocated);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex
            ? { ...row, tcComponentRawMaterialPercentage: value, tcComponentRawMaterialAllocatedWeight: allocated }
            : row
        )
      );
    } else if (columnId === "tcComponentRawMaterialCode") {
      const res = value ? isCertifiedRawMaterialCode(value) : null;
      const certified = value ? (res === null ? "" : res ? "Yes" : "No") : "";
      formik.setFieldValue("tcComponentRawMaterialCode", value);
      formik.setFieldValue("tcComponentRawMaterialCertified", certified);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex
            ? { ...row, tcComponentRawMaterialCode: value, tcComponentRawMaterialCertified: certified }
            : row
        )
      );
    } else {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    }
  };

  const handleAddNewRow = () => {
    const newRow: TCComponentRawMaterialInformation = {
      tcComponentRawMaterialOrder: "",
      tcComponentRawMaterialPercentage: '',
      tcComponentRawMaterialCode: "",
      tcComponentRawMaterialCertified: "",
      tcComponentRawMaterialAllocatedWeight: '',
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true)
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    setData(tableData);
    setRestrictPopUpSave(false)
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  };
  const handleSaveRow = () => {
    if (editingRowIndex === null || editingRowIndex < 0) return;

    const pctNum = Number(formik.values.tcComponentRawMaterialPercentage);
    const otherPct = getTotalPercentage(data, editingRowIndex);
    const newTotalPct = otherPct + (Number.isNaN(pctNum) ? 0 : pctNum);
    if (newTotalPct > 100) {
      const maxPct = 100 - otherPct;
      notify(
        `Total raw material percentage cannot exceed 100%. Current total from other rows: ${otherPct}%. You can enter up to ${maxPct.toFixed(5)}% in this row. Cannot save.`,
        "error"
      );
      return;
    }
    if (!formik.isValid) return;

    const allocated = allocatedWeightFromPercentage(formik.values.tcComponentRawMaterialPercentage);
    const updatedRow = {
      ...formik.values,
      tcComponentRawMaterialCertified:
        formik.values.tcComponentRawMaterialCertified ||
        (formik.values.tcComponentRawMaterialCode
          ? (() => {
              const res = isCertifiedRawMaterialCode(
                formik.values.tcComponentRawMaterialCode
              );
              return res === null ? "" : res ? "Yes" : "No";
            })()
          : ""),
      tcComponentRawMaterialAllocatedWeight: allocated,
    };
    const newData = data.map((row, index) =>
      index === editingRowIndex ? updatedRow : row
    );
    setData(newData);
    setTableData(newData);
    setRawMaterialInfoData(newData);
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const columns: EditableColumnDef<TCComponentRawMaterialInformation>[] = [
    {
      accessorKey: "tcComponentRawMaterialCode",
      header: "Raw Material Code",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Raw Material Code
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
        name="tcComponentRawMaterialCode"
            label=""
            value={value}
            options={rawMaterials}
            enableSearch={true}
            placeholder="Select raw material code"
            onChange={(e) => {
              onChange(e.target.value)
            }}
          />
      ),
    },
    {
      accessorKey: "tcComponentRawMaterialCertified",
      header: "Certified",
      renderEditCell: ({ value }) => (
        <Typography variant="body2" sx={{ py: 1, mb: 2 }}>
          {value === "Yes" ? "Yes" : value === "No" ? "No" : "-"}
        </Typography>
      ),
    },
    {
      accessorKey: "tcComponentRawMaterialPercentage",
      header: "Raw Material Percentage",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Raw Material Percentage
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
                  name="tcComponentRawMaterialPercentage"
                  value={value}
                  inputProps={{ max: 100 }}
                  type="number"
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (!newValue || (Number(newValue) <= 100 && /^[0-9]*\.?[0-9]{0,5}$/.test(newValue))) { onChange(newValue) }
                  }
                  }
                  placeholder="Enter value"
                  sx={{ mb: 2 }}
                />

      ),
    },
    {
      accessorKey: "tcComponentRawMaterialAllocatedWeight",
      header: "Allocated Weight (kg)",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Allocated Weight (kg)
        </span>
      ),
      renderEditCell: ({ value }) => {
        // Display the calculated allocated weight with 5 decimal places
        const displayValue = value != null && value !== "" ? Number(value) : 0;
        return (
          <Typography variant="body2" sx={{ py: 1, mb: 2 }}>
            {displayValue > 0 ? displayValue.toFixed(5) : "0"}
          </Typography>
        );
      },
    },
  ];

  useEffect(() => {
    data.forEach((item: any, index) => {
      item.tcComponentRawMaterialOrder = index + 1;
    });
    updateField("raw-materials-info", data)
  }, [data])

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    const row = data[rowIndex];
    const allocated = allocatedWeightFromPercentage(row.tcComponentRawMaterialPercentage);
    formik.setValues({
      ...row,
      tcComponentRawMaterialAllocatedWeight: allocated,
    });
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setRawMaterialInfoData(updatedData); // Update parent with updated data
    // If a row is deleted, ensure we exit edit mode and re-enable actions.
    if (editingRowIndex === rowIndex) {
      setEditingRowIndex(-1);
      formik.resetForm();
    } else if (editingRowIndex > rowIndex) {
      // Keep the same row "selected" for edit after the array shrinks.
      setEditingRowIndex((prev) => prev - 1);
    }
    setRestrictPopUpSave(false);
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
          <Box>
            <Typography fontWeight={200}>
              Allocate {displayWeight}kg across Raw Materials
            </Typography>
          </Box>
          <StyledButton
            disabled={isRestrictPopUpSave || isTotalPercentage100()}
            onClick={handleAddNewRow}
            sx={{ mb: 2 }}
          >
            Add Raw Material
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default RawMaterialInfo;
